import {
  ChangeDetectionStrategy, Component, DOCUMENT, Injector, OnInit, afterNextRender, computed, effect, inject, input, signal, viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, switchMap } from 'rxjs';
import { FONT_CATEGORIES, FONT_LANGUAGES, FontEntry, fontStack, loadFontCatalogue, loadFontFace } from './font-catalogue';
import { FontPickerPreferencesStore } from './font-picker-preferences';

/** A list row: a section heading, or a font in a section. A favourite font is listed twice, as the old plugin did. */
export type FontRow =
  | {kind: 'divider'; key: string; label: string}
  | {kind: 'font'; key: string; font: FontEntry};

/** The height of every list row, which the virtual scroll needs fixed. */
export const FONT_ROW_HEIGHT = 36;

/**
 * The font dialog of the template editor's font fields: search, language and category filters, an editable sample
 * sentence, and a virtual list of the 1,000 fonts, each in its own face, with favourites and the last picks on top.
 * It closes with the picked family name. Its modal window is the dialog, named by the title's `font-picker-title` id.
 */
@Component({
  selector: 'app-font-picker-dialog',
  templateUrl: './font-picker-dialog.component.html',
  styleUrls: ['./font-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf],
  host: {
    '(keydown)': 'onKeydown($event)',
  },
})
export class FontPickerDialogComponent implements OnInit {
  readonly modal = inject(NgbActiveModal);
  private readonly preferences = inject(FontPickerPreferencesStore);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly announcer = inject(LiveAnnouncer);

  /** The field's current family. */
  readonly value = input('');

  readonly rowHeight = FONT_ROW_HEIGHT;
  readonly categoryNames = FONT_CATEGORIES;
  readonly languages = FONT_LANGUAGES;
  readonly fontStack = fontStack;

  readonly fonts = signal<FontEntry[] | null>(null);
  /** The font list could not be fetched, offline or after a deploy. */
  readonly loadFailed = signal(false);
  readonly search = signal('');
  readonly language = signal(this.preferences.read().language);
  /** The checked category pills; none checked lists every category. */
  readonly categories = signal<string[]>([]);
  readonly favourites = signal(this.preferences.read().favourites);
  /** The row picked by click or arrow key, which Enter and its Select button choose. */
  readonly activeKey = signal<string | null>(null);
  /** The font the sample sentence shows: the last one hovered or picked. */
  readonly sample = signal<FontEntry | null>(null);

  /** Favourites and the last picks, as they were when the dialog opened (the list does not jump on a heart click). */
  private readonly pinned = [...new Set([...this.preferences.read().recent, ...this.preferences.read().favourites])];

  readonly rows = computed<FontRow[]>(() => {
    const fonts = this.fonts() ?? [];
    const search = this.search().trim().toLowerCase();
    const language = this.language();
    const categories = this.categories();
    const shown = fonts.filter((font) => (!language || font.subsets.includes(language))
      && (!categories.length || categories.includes(font.category))
      && (!search || font.family.toLowerCase().includes(search)));
    const byFamily = new Map(shown.map((font) => [font.family, font]));
    const sections: Array<{id: string; label: string; fonts: FontEntry[]}> = [
      {id: 'pinned', label: 'Favorite fonts', fonts: this.pinned.map((family) => byFamily.get(family)).filter(Boolean)},
      {id: 'local', label: 'Local fonts', fonts: shown.filter((font) => font.local)},
      {id: 'google', label: 'Google fonts', fonts: shown.filter((font) => !font.local)},
    ];
    return sections.filter((section) => section.fonts.length).flatMap((section) => [
      {kind: 'divider' as const, key: section.id, label: section.label},
      ...section.fonts.map((font) => ({kind: 'font' as const, key: `${section.id}:${font.family}`, font})),
    ]);
  });

  readonly activeIndex = computed(() => this.rows().findIndex((row) => row.key === this.activeKey()));
  /** The active row's element id, for the search field's and the list's `aria-activedescendant`. */
  readonly activeRowId = computed(() => this.activeIndex() < 0 ? null : `font-picker-row-${this.activeIndex()}`);
  readonly trackRow = (_: number, row: FontRow) => row.key;

  private readonly viewport = viewChild(CdkVirtualScrollViewport);
  /** The rows the virtual scroll has rendered: those on screen and a small buffer. */
  private readonly renderedRange = toSignal(
    toObservable(this.viewport).pipe(switchMap((viewport) => viewport?.renderedRangeStream ?? EMPTY)),
    {initialValue: {start: 0, end: 0}},
  );

  constructor() {
    // Google's stylesheet only for the fonts on screen.
    effect(() => {
      const {start, end} = this.renderedRange();
      for (const row of this.rows().slice(start, end)) {
        if (row.kind === 'font') {
          loadFontFace(row.font, this.document);
        }
      }
    });
  }

  ngOnInit(): void {
    loadFontCatalogue().then((fonts) => {
      this.fonts.set(fonts);
      const current = this.rows().find((row) => row.kind === 'font' && row.font.family === this.value());
      if (current?.kind === 'font') {
        this.activeKey.set(current.key);
        this.sample.set(current.font);
      }
      afterNextRender(() => {
        this.viewport()?.checkViewportSize();
        if (current) {
          this.scrollToRow(current.key, true);
        }
      }, {injector: this.injector});
    }, () => this.loadFailed.set(true));
  }

  toggleCategory(category: string): void {
    this.categories.update((checked) => checked.includes(category)
      ? checked.filter((name) => name !== category)
      : [...checked, category]);
  }

  setLanguage(language: string): void {
    this.language.set(language);
    this.preferences.update({language});
  }

  clearSearch(field: HTMLInputElement): void {
    this.search.set('');
    field.focus();
  }

  isFavourite(font: FontEntry): boolean {
    return this.favourites().includes(font.family);
  }

  toggleFavourite(font: FontEntry): void {
    const favourites = this.isFavourite(font)
      ? this.favourites().filter((family) => family !== font.family)
      : [...this.favourites(), font.family];
    this.favourites.set(favourites);
    this.preferences.update({favourites});
    void this.announcer.announce(`${font.family} ${this.isFavourite(font) ? 'added to' : 'removed from'} favourites`);
  }

  activate(row: FontRow): void {
    if (row.kind === 'font') {
      this.activeKey.set(row.key);
      this.sample.set(row.font);
    }
  }

  choose(font: FontEntry): void {
    this.preferences.addRecent(font.family);
    this.modal.close(font.family);
  }

  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    // The language select and the sample sentence keep their own keys, and a button its Enter.
    if (target.closest('select, [contenteditable], button')) {
      return;
    }
    const active = this.rows()[this.activeIndex()];
    // The row's Favourite and Select buttons are for the mouse: from the keyboard, Shift+Enter (anywhere) or F (in the
    // list, where it types nothing) marks the active font, and Enter selects it.
    const favouriteKey = (event.key === 'Enter' && event.shiftKey)
      || (event.key.toLowerCase() === 'f' && !event.ctrlKey && !event.metaKey && !event.altKey && target.getAttribute('role') === 'listbox');
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (favouriteKey) {
      event.preventDefault();
      if (active?.kind === 'font') {
        this.toggleFavourite(active.font);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active?.kind === 'font') {
        this.choose(active.font);
      }
    }
  }

  /** The next or previous font, wrapping round at either end, as the old plugin's arrow keys did. */
  private move(step: 1 | -1): void {
    const fonts = this.rows().filter((row) => row.kind === 'font');
    if (!fonts.length) {
      return;
    }
    const at = fonts.findIndex((row) => row.key === this.activeKey());
    const next = at < 0
      ? (step > 0 ? fonts[0] : fonts[fonts.length - 1])
      : fonts[(at + step + fonts.length) % fonts.length];
    this.activate(next);
    this.scrollToRow(next.key, false);
  }

  /** Brings a row into view: to the middle when the dialog opens, otherwise only as far as needed. */
  private scrollToRow(key: string, centre: boolean): void {
    const viewport = this.viewport();
    const index = this.rows().findIndex((row) => row.key === key);
    if (!viewport || index < 0) {
      return;
    }
    const top = index * FONT_ROW_HEIGHT;
    const scrolled = viewport.measureScrollOffset('top');
    const height = viewport.getViewportSize();
    if (centre) {
      viewport.scrollToOffset(Math.max(0, top - (height - FONT_ROW_HEIGHT) / 2));
    } else if (top < scrolled) {
      viewport.scrollToOffset(top);
    } else if (top + FONT_ROW_HEIGHT > scrolled + height) {
      viewport.scrollToOffset(top + FONT_ROW_HEIGHT - height);
    }
  }
}
